import { BrowserMetrics } from '../browserMetrics'
import { ConsoleLoggerPlugin } from './plugins/ConsoleLoggerPlugin'
import { IMicroAppPlugin } from './core/types'
import { MicroAppsMonitor } from './MicroAppsMonitor'
import { StateManagerPlugin } from './plugins/StateManagerPlugin'
export class MicroFrontendAppWrapper {
  private static instance: MicroFrontendAppWrapper
  private plugins: IMicroAppPlugin[] = []
  private monitor: MicroAppsMonitor;

  private constructor(monitor: MicroAppsMonitor) {
    this.monitor = monitor;
    // Initialize default plugins
    this.plugins = [new ConsoleLoggerPlugin(), new StateManagerPlugin()]
  }

  static getInstance(monitor: MicroAppsMonitor): MicroFrontendAppWrapper {
    if (!this.instance) {
      this.instance = new MicroFrontendAppWrapper(monitor)
    }
    return this.instance
  }

  wrapApp(createApp: Function, appName: string, filePath: string) {
    return (...args: any[]) => {
      const instanceId = Math.random().toString(36).substring(7); // Generate unique instance ID
      const componentName = args[0]?.name || 'UnknownComponent';

      const startTime = performance.now();

      // Track mount
      const originalMount = createApp(...args);
      const mountTime = performance.now() - startTime;

      BrowserMetrics.getMicroAppsMonitor()?.trackMicroApp({
        appName,
        componentName,
        mountTime,
        action: 'mount',
        filePath,
        instanceId
      });

      // Wrap unmount
      const originalUnmount = originalMount.unmount;
      originalMount.unmount = (...unmountArgs: any[]) => {
        const unmountStartTime = performance.now();
        const result = originalUnmount.apply(originalMount, unmountArgs);
        const unmountTime = performance.now() - unmountStartTime;

        this.monitor?.onUnmount({
          appName,
          componentName,
          action: 'unmount',
          filePath,
          instanceId,
          mountTime,
          unmountTime,
          timestamp: new Date().toISOString(),
        });

        return result;
      };

      return originalMount;
    }
  }

  getPlugin<T extends IMicroAppPlugin>(id: string): T | undefined {
    return this.plugins.find(p => p.getId() === id) as T
  }
}

// Export helper function for the loader
export function wrapApp(createApp: any, appName: string, filePath?: string) {
  return (...args: any[]) => {
    const instanceId = Math.random().toString(36).substring(7); // Generate unique instance ID
    const app = createApp(...args);
    const componentName = args[0]?.__name || args[0]?.name || args[0]?.__file || 'UnknownComponent';

    const startTime = performance.now();

    // Track mount
    const originalMount = app.mount;
    app.mount = (...mountArgs: any[]) => {
      const result = originalMount.apply(app, mountArgs);
      const mountTime = performance.now() - startTime;

      BrowserMetrics.getMicroAppsMonitor()?.onMount({
        appName,
        componentName,
        mountTime,
        action: 'mount',
        filePath,
        instanceId,
        timestamp: new Date().toISOString()
      });

      return result;
    };

    // Track unmount
    const originalUnmount = app.unmount;
    app.unmount = (...unmountArgs: any[]) => {
      const unmountStartTime = performance.now();
      const result = originalUnmount.apply(app, unmountArgs);
      const unmountTime = performance.now() - unmountStartTime;

      BrowserMetrics.getMicroAppsMonitor()?.onUnmount({
        appName,
        componentName,
        unmountTime,
        action: 'unmount',
        filePath,
        instanceId,
        timestamp: new Date().toISOString(),
      });

      return result;
    };

    return app;
  };
}
